import React from 'react';
import { Filter, X } from 'lucide-react';
import { SearchFilters } from '../types/ngap';
import { FILTER_OPTIONS, MESSAGES } from '../constants';

interface FilterPanelProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  isVisible: boolean;
  darkMode: boolean;
  isMobileDrawer?: boolean;
  onClose?: () => void;
  availableData: {
    lettres_cles: string[];
    themes: string[];
    reeducations: string[];
    cotationRange: { min: number; max: number };
  };
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  isVisible,
  darkMode,
  isMobileDrawer = false,
  onClose,
  availableData
}) => {
  const updateFilter = React.useCallback((key: keyof SearchFilters, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  }, [filters, onFiltersChange]);

  const clearFilters = React.useCallback(() => {
    onFiltersChange({
      lettre_cle: '',
      soumis_referentiel: 'all',
      theme: '',
      reeducation: '',
      selected_vignettes: []
    });
  }, [onFiltersChange]);

  const hasActiveFilters = React.useMemo(() => 
    filters.lettre_cle ||
    filters.soumis_referentiel !== 'all' ||
    filters.theme ||
    filters.reeducation ||
    filters.selected_vignettes.length > 0,
    [filters]
  );

  const handleLetterKeyChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFilter('lettre_cle', e.target.value);
  }, [updateFilter]);

  const handleThemeChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFilter('theme', e.target.value);
  }, [updateFilter]);

  const handleReeducationChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFilter('reeducation', e.target.value);
  }, [updateFilter]);

  const handleReferentielChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFilter('soumis_referentiel', e.target.value);
  }, [updateFilter]);

  if (!isVisible) return null;

  const selectClasses = darkMode
    ? 'bg-appines-petrol border-appines-white/20 text-appines-white focus:ring-2 focus:ring-appines-emerald/25 focus:border-appines-emerald hover:bg-appines-petrol/80 transition-colors'
    : 'bg-appines-white border-[rgba(1,42,54,0.2)] text-appines-black focus:ring-2 focus:ring-appines-emerald/25 focus:border-appines-emerald hover:bg-[rgba(1,42,54,0.04)] transition-colors';

  const labelClasses = darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65';

  return (
    <div className={`transition-colors duration-300 ${
      isMobileDrawer 
        ? '' 
        : `rounded-lg p-6 border ${darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'}`
    }`} role="region" aria-label="Filtres de recherche">
      <h3 className="font-semibold flex items-center sr-only" id="filters-heading" role="heading" aria-level="2">
        <Filter className="h-5 w-5 mr-2" />
        Filtres - Affinez vos Résultats
      </h3>
      {(hasActiveFilters || isMobileDrawer) && (
        <div className={`flex items-center justify-end ${isMobileDrawer ? 'mb-6' : 'mb-4'}`}>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-appines-petrol hover:text-appines-emerald text-sm font-medium flex items-center"
              aria-label="Effacer tous les filtres"
            >
              <X className="h-4 w-4 mr-1" />
              {MESSAGES.BUTTONS.CLEAR}
            </button>
          )}
        </div>
      )}

      <div className={`space-y-4 ${isMobileDrawer ? 'space-y-6' : ''}`} role="group" aria-labelledby="filters-heading">
        <div>
          <label htmlFor="lettre-cle-filter" className={`block text-sm font-medium mb-2 ${labelClasses}`}>
            {MESSAGES.LABELS.LETTER_KEY}
          </label>
          <select
            id="lettre-cle-filter"
            value={filters.lettre_cle}
            onChange={handleLetterKeyChange}
            className={`w-full p-3 border rounded-lg ${selectClasses}`}
            aria-label="Filtrer par lettre clé"
          >
            <option value="">{MESSAGES.LABELS.ALL_LETTER_KEYS}</option>
            {availableData.lettres_cles.map(lettre => (
              <option key={lettre} value={lettre}>
                {lettre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="theme-filter" className={`block text-sm font-medium mb-2 ${labelClasses}`}>
            {MESSAGES.LABELS.THEME}
          </label>
          <select
            id="theme-filter"
            value={filters.theme}
            onChange={handleThemeChange}
            className={`w-full p-3 border rounded-lg ${selectClasses}`}
            aria-label="Filtrer par thème"
          >
            <option value="">{MESSAGES.LABELS.ALL_THEMES}</option>
            {availableData.themes.map(theme => (
              <option key={theme} value={theme}>
                {theme}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="reeducation-filter" className={`block text-sm font-medium mb-2 ${labelClasses}`}>
            Type de rééducation
          </label>
          <select
            id="reeducation-filter"
            value={filters.reeducation}
            onChange={handleReeducationChange}
            className={`w-full p-3 border rounded-lg ${selectClasses}`}
            aria-label="Filtrer par type de rééducation"
          >
            <option value="">Tous les types de rééducation</option>
            {availableData.reeducations.map(reeducation => (
              <option key={reeducation} value={reeducation}>
                {reeducation}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="referentiel-filter" className={`block text-sm font-medium mb-2 ${labelClasses}`}>
            {MESSAGES.LABELS.REFERENTIEL}
          </label>
          <select
            id="referentiel-filter"
            value={filters.soumis_referentiel}
            onChange={handleReferentielChange}
            className={`w-full p-3 border rounded-lg ${selectClasses}`}
            aria-label="Filtrer par statut référentiel"
          >
            {FILTER_OPTIONS.REFERENTIEL.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

      </div>
      
      {isMobileDrawer && onClose && (
        <div className={`mt-8 pt-4 border-t ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]'}`}>
          <button
            onClick={onClose}
            className="w-full bg-appines-emerald hover:bg-[#22b886] text-appines-petrol px-4 py-3 rounded-lg transition-colors font-medium"
          >
            Appliquer les filtres
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
